import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	Module,
	Button,
	Form,
	LookupInput,
	FormItem,
	// Icon,
	ParentBoundComponent,
} from 'ps-gui';

import {
	getColumnText,
	getColumnTextMap,
	getPhrase
} from '../data/phrases';
import CatalogRecordingModel, { getSearchOutputColumns } from './catalogrecordingmodel';
import {
	CellBubble,
	CellMainText,
	CellSubText
} from './portalresultsetmodule';
import {
	exportToCsv,
	getGlobalContextChangeChecker
} from './utils';
import { ReactComponent as ModuleLogo } from '../svg/myrecordings.svg';
import {
	ModuleBodySection,
	ModuleHeaderSection,
	ModuleHeader,
	ModuleHeaderBlock
} from '../blocks/modulesections';
import { withGlobalContext } from '../data/globalcontext';
import Message from '../blocks/message';
// import CheckButton from '../components/checkbutton';
import { getLookup } from '../data/lookup';

const Wrapper = styled.div`
`;

const EMPTY_MAP = Immutable.Map({});

class CatalogRecordingModule extends ParentBoundComponent {

	constructor() {
		super();
		this.exportToCsv = this.exportToCsv.bind(this);
		this.setProducerId = this.setProducerId.bind(this);
		this.setDistributionPeriodId = this.setDistributionPeriodId.bind(this);

		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);
	}

	async setProducerId(producerId) {
		let model = this.getModel().setProducerId(producerId);
		this.setModel(await model.refreshSearch());
	}

	async setDistributionPeriodId(dpId) {
		let model = this.getModel().setDistributionPeriodId(dpId);
		this.setModel(await model.refreshSearch());
	}

	exportToCsv() {
		const cols = getSearchOutputColumns(true);
		exportToCsv(this.getModel().getSearch(), {
			outputColumns: cols,
			columnTextMap: getColumnTextMap(cols)
		});
	}

	getModel() {
		return new CatalogRecordingModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	componentDidMount() {
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	renderNoData() {
		return (
			<Message>
				{getPhrase('CatalogRecordingNoDataMessage')}
			</Message>
		);
	}

	async setSource(name, value) {
		this.setModel(await this.getModel().setSource(name, value));
	}

	render() {
		const model = this.getModel();
		const data = this.getData();
		const producerId = data.get('producerId');
		const roleLookup = getLookup('permanentCatalogProducer'); //data.get('roleLookup');
		// const dpLookup = data.get('distributionPeriodLookup.' + producerId);
		// const sources = data.get('sources') || EMPTY_MAP;
		const languageTimestamp = model.getLanguageTimestamp();
		if (!roleLookup) {
			return null;
		}
		if (roleLookup.isEmpty()) {
			return this.renderNoData();
		}
		return (
			<Wrapper>
				<ModuleHeaderSection>
					<ModuleHeaderBlock>
						<ModuleHeader>{getPhrase('CatalogRecording')}</ModuleHeader>
						<Form virtual horizontal>
							<FormItem label={getColumnText('producer')} fixedWidth="470px" marginRight="30px">
								<LookupInput
									getText={roleLookup.getText}
									getRows={roleLookup.getRows}
									combo
									mandatory
									value={producerId}
									onChange={this.setProducerId}
									textRefreshTimestamp={languageTimestamp}
								/>
							</FormItem>
						</Form>
					</ModuleHeaderBlock>
					<ModuleHeaderBlock>
						<ModuleLogo />
					</ModuleHeaderBlock>
				</ModuleHeaderSection>
				<ModuleBodySection>
					<RowCountEtc>
						<RowCount>
							<span className="label">{getPhrase('recordingCount')}:</span>
							<span className="value">{data.getIn([
								'search',
								'data',
								'result',
								'data',
								'pager',
								'totalCount'
							])}</span>
						</RowCount>
						<Button
							type="medium"
							text={getPhrase('exportSelectionToCSV')}
							onAction={this.exportToCsv}
						/>
					</RowCountEtc>
					<QuickFilter>
						<QuickFilterHeader>{getPhrase('displayedRecordings')}</QuickFilterHeader>
					</QuickFilter>
					<Module
						appState={data.get('search')}
						onDataChange={newData => {
							this.setData(data.setIn(['search', 'data'], newData));
						}}
						customResultProps={customResultProps}
					//leftToolBar={this.renderCandidateListToolbar}
					//onResultIconMouseDown={this.onCandidateIconMouseDown}
					/>
				</ModuleBodySection>
			</Wrapper>
		);
	}
}

const QuickFilterHeader = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
	margin-bottom: 8px;
`;

const QuickFilter = styled.div`
	margin-bottom: 24px;
	.checkButton {
		margin-right: 16px;
	}
`;

const RowCountEtc = styled.div`
	margin-bottom: 24px;
`;

const RowCount = styled.div`
	display: inline-block;
	color: ${props => props.theme.disabledTextColor};
	font-size: 20px;
	margin-right: 40px;
	.label {
		font-weight: 400;
		margin-right: 5px;
	}
	.value {
		font-weight: 900;
	}
`;

const FlexCell = styled.div`
	display: flex;
	align-items: center;
`;

const FlexCellContent = styled.div`
	flex-grow: ${props => props.flexGrow};
	flex-shrink: ${props => props.flexShrink};
`;

const cellRendererMap = Object.freeze({
	'title': (cell, row, colIndex) => {
		const subtitle = row.get('subtitle');
		return (
			<FlexCell>
				<FlexCellContent flexGrow="1">
					<CellMainText>{cell}</CellMainText>
					{
						!!subtitle &&
						<CellSubText>{subtitle}</CellSubText>

					}
				</FlexCellContent>
			</FlexCell>
		);
	},
	'production_release_year': (cell, row, colIndex) => {
		if (row.get('is_release_year')) {
			return '(' + cell + ')';
		} else {
			return cell;
		}
	},
	'audio_video': (cell, row, colIndex) => {
		if (!cell) {
			return null;
		} else if (cell !== 'A' && cell !== 'V') {
			cell = 'Other';
		}
		const id = 'recordingType' + cell;
		return (
			<CellBubble className={id}>
				{getPhrase(id)}
			</CellBubble>
		);
	},
});

const customResultProps = {
	cellRendererMap,
	rowCountLabel: () => getPhrase('recordingCount'),
	defaultColumnWidths: {
		'audio_video': '140px',
		'length': '200px',
		'isrc': '160px',
		'production_release_year': '180px'
	},
	columnFilters: {
		'audio_video': {
			type: 'lookup',
			lookup: 'audioVideo'
		},
		'isrc': {
			type: 'text'
		},
		'length': {
			type: 'runtime'
		},
		'production_release_year': {
			type: 'number',
			groupless: true,
			inputSize: 4,
		},
		'title': {
			type: 'text'
		}
	}
};

export default withGlobalContext(CatalogRecordingModule);
